import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useEffect, useState } from 'react';
import queryString from 'query-string';
import { useUserAlias } from 'src/data/api/orbit-service-ts/user/queries/useUserAlias';

/* Object containing user authentication info */
export interface MidwayTokenInfo {
  decodedToken: any;
  userAlias: string;
  expiryTime: number;
}

interface MidwayRequestParams {
  scope: string;
  response_type: string;
  client_id: string;
  redirect_uri: string;
  nonce: string;
  sentry_handler_version?: string;
}

const useMidway = () => {
  const [_, setUserAlias] = useUserAlias();
  const [midwayTokenInfo, setMidwayTokenInfo] = useState<MidwayTokenInfo>({
    userAlias: '',
    decodedToken: {},
    expiryTime: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const midwayUrl = 'https://midway-auth.amazon.com/SSO';

  /** Used to ensure every request is unique */
  function generateNonce() {
    return crypto.getRandomValues(new Uint32Array(5)).toString().replace(/,/g, '');
  }

  function buildMidwayRequestUrl(shouldRedirect = false): string {
    const params: MidwayRequestParams = {
      scope: 'openid',
      response_type: 'id_token',
      client_id: `https://${window.location.hostname}:${window.location.port || '443'}`,
      redirect_uri: `${window.location.protocol}//${window.location.hostname}:${window.location.port || '443'}/`,
      nonce: generateNonce(),
    };
    if (!shouldRedirect) {
      params.sentry_handler_version = 'MidwayNginxModule-1.3-1';
    }
    const baseUrl = shouldRedirect ? `${midwayUrl}/redirect?` : `${midwayUrl}?`;
    return baseUrl + queryString.stringify(params);
  }

  /*
   * Fetch a token from Midway without redirection
   */
  async function fetchToken(): Promise<string | undefined> {
    const midwayRequestUrl = buildMidwayRequestUrl();
    const response = await fetch(midwayRequestUrl, { credentials: 'include' });
    if (!response.ok) {
      if (response.status === 401) {
        // Redirecting to Midway because session has expired
        console.error('Unauthorized');
        window.location.href = buildMidwayRequestUrl(true);
      } else {
        throw new Error(`${response.status}`);
      }
    }

    const token = response.text();

    return token;
  }

  const processToken = async (token: string) => {
    try {
      const decoded: JwtPayload = jwtDecode(token);

      const tokenInfo: MidwayTokenInfo = {
        decodedToken: decoded,
        userAlias: decoded.sub || '',
        expiryTime: decoded.exp || 0,
      };
      setUserAlias(tokenInfo.userAlias);
      setMidwayTokenInfo(tokenInfo);
      setIsLoading(false);
      setIsError(false);
    } catch (err) {
      setIsError(true);
      const errorMessage =
        err instanceof Error ? err : new Error('Failed to decode token');
      console.error('Failed to decode token:', errorMessage);
      setError(errorMessage);
    }
  };

  const cleanupUrl = () => {
    // Remove token from URL after processing
    const url = new URL(window.location.href);
    if (url.searchParams.has('id_token')) {
      url.searchParams.delete('id_token');
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  useEffect(() => {
    const initializeMidway = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        // If we're on the Midway redirect URL, force redirect back to app
        if (window.location.hostname === 'midway-auth.amazon.com') {
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUri = urlParams.get('redirect_uri');
          const token = urlParams.get('id_token');

          if (redirectUri && token) {
            window.location.href = `${redirectUri}?id_token=${token}`;
            return;
          }
        }

        // Check if token is in URL (redirect from Midway)
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('id_token');

        if (urlToken) {
          await processToken(urlToken);
          // Delay cleanup to allow session establishment
          setTimeout(() => {
            cleanupUrl();
          }, 2000);
        } else {
          const token = await fetchToken();
          if (token) {
            await processToken(token);
          }
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err : new Error('Failed to initialize Midway');
        console.error('Failed to initialize Midway:', errorMessage);
        setIsError(true);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    initializeMidway();
  }, []);

  return {
    isLoading,
    isError,
    error,
    userAlias: midwayTokenInfo.userAlias,
    midwayTokenInfo,
  };
};

export default useMidway;
